import { useCallback, useState } from "react";
import { supabase } from "@/lib/supabase";

export interface AvatarUploadResult {
  url: string | null;
  error: string | null;
}

export interface AvatarUploadProgress {
  uploading: boolean;
  fileName: string | null;
}

const BUCKET = "article-images";
const MAX_SIZE_MB = 5;
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];

function sanitizeFileName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9.\-_]/g, "-")
    .replace(/-+/g, "-");
}

function buildAvatarPath(file: File, authorSlug: string): string {
  const ts = Date.now();
  const ext = file.name.split(".").pop() || "jpg";
  const safe = sanitizeFileName(authorSlug);
  return `avatars/${safe}-${ts}.${ext}`;
}

export function useAuthorAvatarUpload() {
  const [progress, setProgress] = useState<AvatarUploadProgress>({
    uploading: false,
    fileName: null,
  });

  const validateFile = (file: File): string | null => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return "Nur JPG, PNG und WEBP erlaubt.";
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      return `Maximale Dateigrösse: ${MAX_SIZE_MB} MB.`;
    }
    return null;
  };

  const uploadAvatar = useCallback(
    async (file: File, authorSlug: string): Promise<AvatarUploadResult> => {
      setProgress({ uploading: true, fileName: file.name });

      const validationError = validateFile(file);
      if (validationError) {
        setProgress({ uploading: false, fileName: null });
        return { url: null, error: validationError };
      }

      const path = buildAvatarPath(file, authorSlug);

      const { error: uploadError } = await supabase.storage
        .from(BUCKET)
        .upload(path, file, {
          cacheControl: "3600",
          upsert: true,
          contentType: file.type,
        });

      if (uploadError) {
        setProgress({ uploading: false, fileName: null });
        return { url: null, error: uploadError.message };
      }

      const { data: urlData } = supabase.storage
        .from(BUCKET)
        .getPublicUrl(path);
      const publicUrl = urlData?.publicUrl ?? null;

      setProgress({ uploading: false, fileName: null });

      if (!publicUrl) {
        return { url: null, error: "Konnte keine öffentliche URL abrufen." };
      }

      return { url: publicUrl, error: null };
    },
    []
  );

  const removeAvatar = useCallback(
    async (imageUrl: string): Promise<{ error: string | null }> => {
      try {
        const url = new URL(imageUrl);
        const pathMatch = url.pathname.match(/\/avatars\/(.+)$/);
        if (!pathMatch) return { error: null };
        const path = `avatars/${pathMatch[1]}`;
        const { error } = await supabase.storage.from(BUCKET).remove([path]);
        if (error) return { error: error.message };
        return { error: null };
      } catch {
        return { error: null };
      }
    },
    []
  );

  return { uploadAvatar, removeAvatar, progress };
}